import { formatPrice } from '@/components/ui/Price';
import type { WaiterLoyaltyRewardType } from '../waiterApi';

/**
 * Fidélité — les règles pures du « Scanner » du carnet serveur. Même
 * normalisation que hub (`normalize_code`) et que le scanner owner/admin :
 * majuscules, sans espaces ni tirets, sans le préfixe `OTL:` des QR.
 */
export function normalizeLoyaltyCode(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase().replace(/^OTL:/, '').replace(/-/g, '');
}

/** Carte (10), coupon (8), ancien coupon (16 hex) : rien d'autre n'est un code. */
export function isLoyaltyCode(normalized: string): boolean {
  return /^[A-Z0-9]{4,32}$/.test(normalized);
}

/**
 * « −5 € » / « −10 % » (vrai signe moins) ; '' pour une récompense qui n'est
 * pas une remise. `signed: false` pour une phrase qui dit déjà qu'on retire
 * (« Retirez 5 € de l'addition », pas « Retirez −5 € »).
 */
export function loyaltyValueLabel(
  type: WaiterLoyaltyRewardType,
  value: string | null,
  locale = 'fr',
  signed = true,
): string {
  if (value == null || value === '') return '';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  const text = amount.toLocaleString(locale, { maximumFractionDigits: 2 });
  const sign = signed ? '\u2212' : '';
  // Espace insécable : « −5 » ne doit jamais finir une ligne sans son « € ».
  if (type === 'percent') return `${sign}${text}\u00A0%`;
  if (type === 'fixed') return `${sign}${text}\u00A0€`;
  return '';
}

/**
 * Un montant glissé dans une phrase traduite, isolé de gauche à droite : en
 * arabe, « −5 € » était réordonné en « € 5− ». Dans le JSX, `<bdi dir="ltr">`
 * fait la même chose.
 */
export function isolateLtr(text: string): string {
  return `\u2066${text}\u2069`;
}

/** Les refus que hub nomme et que l'écran sait dire (`waiter.loyaltyScan.errors.<code>`). */
const KNOWN_ERRORS = [
  'not_found',
  'disabled',
  'insufficient_points',
  'out_of_stock',
  'max_uses_reached',
  'already_used',
  'expired',
  // SC-1638 — « Créditer une addition » et son annulation.
  'bill_open',
  'bill_pending',
  'already_credited_other',
  'empty_bill',
  'invalid_amount',
  'too_late',
  'already_cancelled',
] as const;

export type LoyaltyScanError =
  | (typeof KNOWN_ERRORS)[number]
  | 'bill_not_found'
  | 'credit_spent'
  | 'offline'
  | 'generic';

/**
 * Ce que dit une erreur RTK. `FETCH_ERROR` = la requête n'est jamais partie
 * (pas de réseau) ; 502 HUB_UNAVAILABLE et tout le reste = « réessayez ».
 */
export function loyaltyScanError(error: unknown): LoyaltyScanError {
  const err = error as { status?: unknown; data?: { detail?: unknown } } | null | undefined;
  if (err?.status === 'FETCH_ERROR') return 'offline';
  const detail = err?.data?.detail;
  if (typeof detail === 'string' && (KNOWN_ERRORS as readonly string[]).includes(detail)) {
    return detail as LoyaltyScanError;
  }
  return 'generic';
}

/**
 * SC-1638 — un refus de « Créditer » ou de « Annuler ce crédit », dit pour une
 * addition : `not_found` y veut dire que l'addition n'est plus proposée (trop
 * ancienne, absorbée par une autre note), et `insufficient_points` que les
 * points du crédit ont déjà été dépensés — pas « code inconnu » ni « pour cette
 * récompense ».
 */
export function loyaltyCreditError(error: unknown): LoyaltyScanError {
  const code = loyaltyScanError(error);
  if (code === 'not_found') return 'bill_not_found';
  if (code === 'insufficient_points') return 'credit_spent';
  return code;
}

/** « 42,50 € » — le montant d'une addition tel que sale_api l'envoie ('42.50'). */
export function billAmountLabel(amount: string): string {
  const value = Number(amount);
  return Number.isFinite(value) ? formatPrice(Math.round(value * 100)) : '';
}

/** Motif d'un coupon refusé à la lecture → la même famille de messages. */
export function loyaltyReasonError(reason: string): LoyaltyScanError {
  return (KNOWN_ERRORS as readonly string[]).includes(reason) ? (reason as LoyaltyScanError) : 'not_found';
}

/** « 12 oct. » — l'année seulement quand ce n'est pas celle en cours. */
export function formatLoyaltyDate(iso: string, locale: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}
